// src/components/Skeleton.jsx
//
// Skeleton loader minimalista (sin dependencias). Tres variantes:
//   <Skeleton />              → línea (text placeholder)
//   <Skeleton variant="block" /> → bloque rectangular
//   <Skeleton variant="circle" /> → círculo (avatares)
//
// Props comunes: width, height, count (multiplica), style, className.
//
// Convenciones:
//   - Usa CSS variables del tema (--c-input-bg / --c-border) para que
//     funcione en dark/light mode sin sobrescribir.
//   - La animación shimmer se inyecta una sola vez al montar.

import React from 'react';

const STYLE_TAG_ID = 'skeleton-shimmer-style';
function injectShimmerStyle() {
    if (typeof document === 'undefined') return;
    if (document.getElementById(STYLE_TAG_ID)) return;
    const tag = document.createElement('style');
    tag.id = STYLE_TAG_ID;
    tag.innerHTML = `
@keyframes skeleton-shimmer {
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
.skeleton {
  display: inline-block;
  background: linear-gradient(
    90deg,
    var(--c-input-bg, #f3f4f6) 0%,
    var(--c-border, #e5e7eb) 50%,
    var(--c-input-bg, #f3f4f6) 100%
  );
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
  vertical-align: middle;
}
.skeleton.skeleton-line   { display: block; height: 1em; margin: 0.4em 0; }
.skeleton.skeleton-block  { display: block; width: 100%; }
.skeleton.skeleton-circle { border-radius: 50%; }
@media (prefers-reduced-motion: reduce) {
  .skeleton { animation: none; }
}
`;
    document.head.appendChild(tag);
}

export default function Skeleton({
    variant = 'line',
    width,
    height,
    count = 1,
    style = {},
    className = '',
}) {
    React.useEffect(injectShimmerStyle, []);

    const baseStyle = { width, height, ...style };
    const cls = `skeleton skeleton-${variant} ${className}`.trim();

    if (count === 1) {
        return <span className={cls} style={baseStyle} aria-hidden="true" />;
    }
    return (
        <>
            {Array.from({ length: count }).map((_, i) => (
                <span key={i} className={cls} style={baseStyle} aria-hidden="true" />
            ))}
        </>
    );
}

/** Helper: tarjeta tipo KPI (label + valor grande) */
export function SkeletonKpi() {
    return (
        <div style={{
            background: 'var(--c-card)',
            border: '1px solid var(--c-border)',
            borderRadius: 12,
            padding: '16px 18px',
            minHeight: 86,
        }}>
            <Skeleton variant="line" width="40%" height={10} />
            <div style={{ marginTop: 10 }}>
                <Skeleton variant="line" width="55%" height={26} />
            </div>
        </div>
    );
}

/** Helper: fila de tabla (avatar + 3 columnas) */
export function SkeletonRow({ withAvatar = false }) {
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 8px',
            borderBottom: '1px solid var(--c-border)',
        }}>
            {withAvatar && <Skeleton variant="circle" width={32} height={32} />}
            <div style={{ flex: 1 }}>
                <Skeleton variant="line" width="60%" height={12} />
                <Skeleton variant="line" width="35%" height={10} />
            </div>
            <Skeleton variant="line" width={60} height={12} />
        </div>
    );
}
